import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useLogin } from "react-facebook";
import { useTranslation } from "react-i18next";
import {
  loginAnalyticsTracking,
  TRACKING_ACTIONS,
  TRACKING_ERROR_CODES,
  TRACKING_TYPES,
  trackSysConnect,
} from "~/app/services/scopely/loginAnalyticsTracking";
import FacebookIcon from "~/assets/icons/facebook_icon_small.svg";
import { useAuth } from "~/hooks/useAuth";
import { useAuthStore } from "~/stores/auth";
import { LoginButton } from "./LoginButton";

interface LoginWithFacebookProps {
  handleCloseMenuLogin?: (event: Event | React.SyntheticEvent) => void;
  onError?: (error: Error) => void;
}

export function LoginWithFacebook({ handleCloseMenuLogin, onError }: LoginWithFacebookProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login: fbLogin } = useLogin();
  const { setAccountLogin } = useAuthStore();
  const { ssoLogin, isLoading } = useAuth({
    onError,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: old code
  const handleClick = useCallback(async () => {
    setAccountLogin("facebook");

    try {
      const response = await fbLogin({
        scope: "email",
      });

      const fbAuthToken = response.authResponse.accessToken;

      if (fbAuthToken) {
        await ssoLogin(fbAuthToken, 1); // 1 for Facebook
      }
    } catch (error: unknown) {
      loginAnalyticsTracking({
        type: TRACKING_TYPES.FACEBOOK,
        action: TRACKING_ACTIONS.LOGIN_FAILED,
      });
      trackSysConnect({
        type: "facebook",
        errorCode: `${TRACKING_ERROR_CODES.FACEBOOK_RESPONSE_ERROR}`,
      });

      // Not game account linked to facebook is found
      if ((error as Error & { Code?: number })?.Code === 4003) {
        onError?.(error as Error);
        navigate({
          to: "/home",
          search: { accountNotLinked: true },
          replace: true,
          reloadDocument: true,
        });
      }
    }
  }, [setAccountLogin, fbLogin, ssoLogin, onError]);

  return (
    <LoginButton
      onClick={(e) => {
        if (handleCloseMenuLogin) {
          handleCloseMenuLogin(e);
        }
        handleClick();
      }}
      disabled={isLoading}
      data-testid="facebook-btn"
      startIcon={
        <img
          src={FacebookIcon}
          style={{ width: "24px", height: "24px", flex: "1 0 24px" }}
          alt="Facebook Logo"
        />
      }
      sx={{
        justifyContent: "start",
        backgroundColor: "#1877F2",
        "&:hover": {
          backgroundColor: "#1666D5",
        },
      }}
    >
      {t("sign.facebook")}
    </LoginButton>
  );
}
